import { z } from "zod";

const cursorPayload = z.strictObject({ g: z.number().int().positive(), p: z.number().int().nonnegative() });

export interface CursorPosition {
  generation: number;
  position: number;
}

export function encodeCursor(c: CursorPosition): string {
  return Buffer.from(JSON.stringify({ g: c.generation, p: c.position }), "utf8").toString("base64url");
}

export function decodeCursor(cursor: string): CursorPosition | null {
  try {
    const parsed = cursorPayload.safeParse(JSON.parse(Buffer.from(cursor, "base64url").toString("utf8")));
    return parsed.success ? { generation: parsed.data.g, position: parsed.data.p } : null;
  } catch {
    return null;
  }
}

export type CursorCheck =
  { outcome: "valid"; position: number } | { outcome: "expired" | "unrestorable" | "invalid"; message: string };

export function checkCursor(
  cursor: string,
  env: { generation: number; retainedFromPosition: number; headPosition: number },
): CursorCheck {
  const decoded = decodeCursor(cursor);
  if (!decoded) return { outcome: "invalid", message: "Malformed cursor" };
  if (decoded.generation !== env.generation) {
    return { outcome: "unrestorable", message: "Cursor from another environment generation; take a new snapshot" };
  }
  if (decoded.position > env.headPosition) {
    return { outcome: "unrestorable", message: "Cursor is ahead of the retained history; take a new snapshot" };
  }
  if (decoded.position < env.retainedFromPosition) {
    return {
      outcome: "expired",
      message: `History before position ${env.retainedFromPosition} is not retained; take a new snapshot`,
    };
  }
  return { outcome: "valid", position: decoded.position };
}
